
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building, User, FileText } from 'lucide-react';
import documentsService from '@/services/documentsService';
import { propertiesService } from '@/services/propertiesService';

const DocumentAssignmentModal = ({ isOpen, onClose, document, onUpdate }) => {
  const [propertyId, setPropertyId] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [documentName, setDocumentName] = useState('');
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (document) {
      setPropertyId(document.property_id);
      setTenantId(document.tenant_id);
      setDocumentName(document.name || '');
    }
  }, [document]);

  useEffect(() => {
    const loadData = async () => {
      const [props, tens] = await Promise.all([
        propertiesService.getProperties(),
        propertiesService.getTenants(),
      ]);
      setProperties(props || []);
      setTenants(tens || []);
    };
    loadData();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await documentsService.updateDocumentAssignment(document.id, propertyId, tenantId, documentName);
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Failed to update document assignment', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        <DialogHeader>
          <DialogTitle className="text-gray-900 dark:text-gray-100">Dokument zuweisen</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="documentName" className="text-right col-span-1 flex items-center text-gray-700 dark:text-gray-300">
              <FileText className="h-4 w-4 mr-2" />
              Name
            </label>
            <input
              id="documentName"
              type="text"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              className="col-span-3 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Dokumentenname eingeben"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="property" className="text-right col-span-1 flex items-center text-gray-700 dark:text-gray-300">
              <Building className="h-4 w-4 mr-2" />
              Objekt
            </label>
            <Select value={propertyId || 'none'} onValueChange={setPropertyId}>
              <SelectTrigger className="col-span-3 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                <SelectValue placeholder="Objekt auswählen" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600">
                <SelectItem value="none" className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-600">Kein Objekt</SelectItem>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id} className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-600">
                    {prop.property_name || prop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <label htmlFor="tenant" className="text-right col-span-1 flex items-center text-gray-700 dark:text-gray-300">
              <User className="h-4 w-4 mr-2" />
              Mieter
            </label>
            <Select value={tenantId || 'none'} onValueChange={setTenantId}>
              <SelectTrigger className="col-span-3 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                <SelectValue placeholder="Mieter auswählen" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600">
                <SelectItem value="none" className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-600">Kein Mieter</SelectItem>
                {tenants.map((ten) => (
                  <SelectItem key={ten.id} value={ten.id} className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-600">
                    {ten.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="bg-gray-50 dark:bg-gray-800 pt-4">
          <Button 
            variant="outline" 
            onClick={onClose} 
            className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
          >
            Abbrechen
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isSaving} 
            className="bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-600 dark:hover:bg-blue-700"
          >
            {isSaving ? 'Speichern...' : 'Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentAssignmentModal;

